import { observer, PropTypes as MobxPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import b from 'b_';
import AdditionalConditionsStore from '../../../stores/Policy/FileRules/AdditionalConditionsStore';
import { Card, FTBody, FTLabel } from '../../Fuse';
import PolicySectionHeader from '../PolicySectionHeader/PolicySectionHeader';
import PolicySection from '../PolicySection';
import { Or } from '../ConnectionBlock/ConnectionBlock';
import FuseButton from '../../FuseControls/FuseButton';
import FuseReadOnlyRadioButton from '../../FuseControls/FuseReadOnlyRadioButton';
import FuseReadOnlySelect from '../../FuseControls/FuseReadOnlySelect';
import FuseConsoleCardFooter from '../../ConsoleFragments/FuseConsoleCardFooter';
import './AdditionalConditions.css';
import { RULE_REC_TYPE } from '../../../constants';

const block = b.lock('AdditionalConditions');

class AdditionalConditions extends Component {
    static propTypes = {
        store: AdditionalConditionsStore.PropType.isRequired,
        readOnly: PropTypes.bool,
        showMalwareCondition: PropTypes.bool,
        showDetectorSetsCondition: PropTypes.bool,
        scanFileSize: PropTypes.number.isRequired,
        fileScanType: PropTypes.string.isRequired,
        supportedFileTypes: MobxPropTypes.arrayOrObservableArray.isRequired,
        title: PropTypes.string,
    };

    state = {
        supportedFileTypesGuideOpen: false,
    }

    onSupportedFileTypesGuideToggle = () => {
        this.setState({ supportedFileTypesGuideOpen: !this.state.supportedFileTypesGuideOpen });
    }

    render() {
        const { supportedFileTypesGuideOpen } = this.state;
        const {
            store,
            showMalwareCondition,
            showDetectorSetsCondition,
            scanFileSize,
            fileScanType,
            supportedFileTypes,
            title
        } = this.props;
        let { readOnly } = this.props;
        const policyProps = {
            grayBackground: !readOnly,
            readOnly
        };
        readOnly = readOnly || store.isDefault;
        const titleFileScanType = fileScanType[0].toUpperCase() + fileScanType.slice(1);
        const filesHelpText = `Files that are not supported by the ${fileScanType} scan or are too large are blocked.
                        Max file size for ${fileScanType} scan is ${scanFileSize} MB.`;

        return <Card title={title}>
            {store.ruleType === RULE_REC_TYPE.FILE_THREAT_RULES ? null : <Fragment>
                <PolicySection {...policyProps}>
                    <PolicySectionHeader
                        title="Unsupported & Encrypted Files"
                        tooltipText={'Define the rule action if a file is encrypted or unsupported ' +
                        '(file type or file size). '}
                    >
                        <span className={block('Space')} />
                        {!readOnly ? <FuseButton
                            color={'blue'}
                            icon="help_outline"
                            type={'tertiary'}
                            disabled={supportedFileTypesGuideOpen}
                            onClick={this.onSupportedFileTypesGuideToggle}
                            qa={'help_button'}
                        >Supported File Types</FuseButton> : null}
                    </PolicySectionHeader>

                    {supportedFileTypesGuideOpen && !readOnly ? <div className={block('SupportedFileTypesGuide')}>
                        <FuseButton
                            icon="close"
                            primary
                            className={block('CloseButton')}
                            onClick={this.onSupportedFileTypesGuideToggle}
                        />
                        <FTBody className={block('Marked')}>File {titleFileScanType} Scan - Supported Files</FTBody>
                        <p className={block('InfoText')}>
                            {filesHelpText}
                            <br /><br />
                            <span className={block('Marked')}>{titleFileScanType} Scan supported file types:</span>
                            <br />
                            {supportedFileTypes.join(', ')}
                            <br /><br />
                            <span className={block('Marked')}>
                                Please Note - supported file types and size maximums are subject to change.
                            </span>
                        </p>
                    </div> : null}

                    <FuseReadOnlyRadioButton
                        value
                        label={'Block unsupported & encrypted file types'}
                        readOnly={readOnly}
                        store={store.blockUnsupportedFiles}
                        qa="block-unsupported-files-radio"
                    />

                    <FuseReadOnlyRadioButton
                        value={false}
                        label={'Allow unsupported & encrypted file types'}
                        readOnly={readOnly}
                        store={store.blockUnsupportedFiles}
                        qa="allow-unsupported-files-radio"
                    />

                    {!readOnly ? <FuseConsoleCardFooter
                        className={block('SectionFooter')}
                        text={filesHelpText}
                    /> : null}
                </PolicySection>
                {!readOnly && <Or/>}
            </Fragment>}

            {showMalwareCondition ? <Fragment>
                <PolicySection {...policyProps}>
                    <PolicySectionHeader
                        title="Malware"
                        tooltipText={'Define the rule action if a file is found to contain malware.'}
                        isFixedTooltip
                    />

                    <FuseReadOnlyRadioButton
                        value
                        label={'Block if Malware detected (File allowed if no malware detected)'}
                        readOnly={readOnly}
                        store={store.blockMalware}
                        qa="block-malware-radio"
                    />

                    <FuseReadOnlyRadioButton
                        value={false}
                        label={'Allow if Malware detected (File allowed if no malware detected)'}
                        readOnly={readOnly}
                        store={store.blockMalware}
                        qa="allow-malware-radio"
                    />
                    <FuseConsoleCardFooter
                        wrapperStyle={{ marginTop: '20px' }}
                        text={'The system will generate an alert in the ‘Analytics’ ' +
                            'application when Malware is detected.'}
                    />
                </PolicySection>

                {!readOnly && <Or/>}
            </Fragment> : null}

            {showDetectorSetsCondition ? <Fragment>
                <PolicySection {...policyProps}>
                    <PolicySectionHeader
                        title="Content Matching"
                        tooltipText={'Define the rule action based on your Detector Sets. ' +
                        '(Detector sets are groups of detectors. Detectors are used to identify information ' +
                        'that might be sensitive, such as drivers\' license numbers, social security ' +
                        'numbers or credit card numbers). '}
                    />

                    <FuseReadOnlyRadioButton
                        value
                        label={'Block if file content matches the Detector Set (File allowed if no matches detected)'}
                        readOnly={readOnly}
                        store={store.blockOnDetection}
                        qa="block-detector-sets-radio"
                    />

                    <FuseReadOnlyRadioButton
                        value={false}
                        label={'Allow if file content matches the Detector Set (File allowed if no matches detected)'}
                        readOnly={readOnly}
                        store={store.blockOnDetection}
                        qa="allow-detector-set-radio"
                    />

                    <FuseReadOnlySelect
                        readOnly={readOnly}
                        store={store.detectorSetsSelect}
                        fullWidth
                        searchable
                        containerClassName={block('SelectorContainer')}
                        placeholder="Select Detector Sets"
                        noOptionsLabel="No entries found"
                    />
                    {store.hasDeletedDetectorSet && !readOnly ? <FTLabel className={block('DeletedLegend')}>
                        Detector Sets displayed in red have been deleted from the system.
                    </FTLabel> : null}

                    {!readOnly ? <FuseConsoleCardFooter
                        className={block('SectionFooter')}
                        text="Detector Sets are managed in the ‘Data Loss Prevention’ application.
                            The system will generate an alert in the ‘Analytics
                            application when a Detector set is matched."
                    /> : null}
                </PolicySection>

                {!readOnly && <Or/>}
            </Fragment> : null}

            <PolicySection {...policyProps}>
                <PolicySectionHeader
                    title="Timeout and Errors"
                    tooltipText={'Define the rule action if the file scanning operation does not complete ' +
                    '(due to a timeout or some other error).'}
                />

                <FuseReadOnlyRadioButton
                    value
                    label={'Block files after a timeout or error'}
                    readOnly={readOnly}
                    store={store.blockOnTimeout}
                    qa="block-timeout-radio"
                />

                <FuseReadOnlyRadioButton
                    value={false}
                    label={'Allow files after a timeout or error'}
                    readOnly={readOnly}
                    store={store.blockOnTimeout}
                    qa="allow-timeout-radio"
                />
            </PolicySection>
        </Card>;
    }
}

export default observer(AdditionalConditions);
